// Strict postMessage integration for embedding the game in an operator iframe.
// Every outbound message targets an explicit allowed origin (never "*"), and
// every inbound message is rejected unless its origin is on the allowlist. This
// prevents cross-origin frames from injecting commands or reading game events.
/**
 * Validates that the current frame is embedded by an allowed parent origin.
 * Returns the matched origin, or null if not embedded / not allowed. Uses
 * document.referrer because a cross-origin parent's location is not readable.
 */
export function resolveParentOrigin(allowed, referrer) {
    if (allowed.includes('*')) {
        throw new Error('allowedParentOrigins must not contain "*"');
    }
    if (!referrer)
        return null;
    let refOrigin;
    try {
        refOrigin = new URL(referrer).origin;
    }
    catch {
        return null;
    }
    return allowed.includes(refOrigin) ? refOrigin : null;
}
export class ParentBridge {
    allowed;
    target;
    source;
    listener;
    constructor(cfg) {
        if (cfg.allowedParentOrigins.length === 0 || cfg.allowedParentOrigins.includes('*')) {
            throw new Error('allowedParentOrigins must be a non-empty list without "*"');
        }
        this.allowed = [...cfg.allowedParentOrigins];
        this.target = cfg.targetWindow ?? (typeof window !== 'undefined' ? window.parent : undefined);
        this.source = cfg.eventSource ?? (typeof window !== 'undefined' ? window : undefined);
    }
    /** Posts a message to each allowed parent origin (explicit targets only). */
    post(msg) {
        if (!this.target)
            return;
        for (const origin of this.allowed) {
            this.target.postMessage(msg, origin);
        }
    }
    /** Subscribes to inbound parent messages, ignoring disallowed origins. */
    onMessage(handler) {
        if (!this.source)
            return () => undefined;
        const listener = (ev) => {
            if (!this.allowed.includes(ev.origin))
                return; // strict origin check
            const msg = ev.data;
            if (!msg || typeof msg !== 'object' || typeof msg.type !== 'string')
                return;
            handler(msg);
        };
        this.listener = listener;
        this.source.addEventListener('message', listener);
        return () => this.dispose();
    }
    dispose() {
        if (this.listener && this.source) {
            this.source.removeEventListener('message', this.listener);
            this.listener = undefined;
        }
    }
}
//# sourceMappingURL=postMessage.js.map